import {
  Badge,
  Column,
  Loading,
  Panel,
  Table,
  Tooltip,
} from "@appkit4/react-components";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import CompanyPanel from "../components/CompanyPanel";
import toast from "../components/DismissibleToast";
import { BackButton, NextButton } from "../components/ProgressNav";
import { Button, Switch } from "../components/ReadonlyAwareInputs";
import CustomTooltipIcon from "../components/customTooltipIcon/customTooltipIcon";
import { AuthContext } from "../services/AuthProvider";
import {
  canProgressCalculation,
  disableProgressInViewMode,
  getCurrentPeriodIsReadonly,
  getGroupData,
  getGroupID,
  getPageTooltips,
  getPoaIndex,
  setGroupData,
} from "../services/GroupContext";
import { getBFDTotal } from "../services/calculations/ComponentTotals";
import { calculateUsedAllowances } from "../services/calculations/allowanceCalc";
import { getReviewCalc2Values } from "../services/calculations/calc2";
import { getExcessDebtCapPriorPeriod } from "../services/calculations/excessDebtCap";
import sqlService from "../services/sqldatabase/sqldatabase.service";
import { store } from "../store/store";
import { processDateJS } from "../utils/dateProcessor";
import RevCalcHeader from "./ReviewCalcHeader";
import ReviewCalcNav from "./revCalcNavigation";
dayjs.extend(utc);

const BACKEND_URL = process.env.REACT_APP_BACKEND_API;

const getFixedRatioMetrics = (tooltips) => {
  return {
    "Fixed ratio %": tooltips.fixedRatio,
    "Aggregate Tax EBITDA": tooltips.aggregateTaxEBITDAFR,
    "Fixed Ratio % of Tax EBITDA": tooltips.fixedRatioTaxEBITDA,
    "Adjusted Net Group Interest Expense (ANGIE)": tooltips.ANGIE,
    "Excess debt cap brought forward": tooltips.excessDeptCapGR,
    "Fixed Ratio Debt Cap (s400(1))": tooltips.fixedRatioDeptCap,
    "Basic Interest Allowance (s397(1))": tooltips.basicInterestAllowanceFR,
    "Aggregate Net Tax Interest Income (ANTII)": tooltips.ANTIIFR,
    "Interest allowance (s396(1))": tooltips.interestAllowanceFR,
  };
};

const getGroupRatioMetrics = (tooltips) => {
  return {
    "Qualifying Net Group Interest Expense (QNGIE)": tooltips.QNGIE,
    "Group EBITDA": tooltips.groupEBITDA,
    "Group Ratio %": tooltips.groupRatio,
    "Aggregate Tax EBITDA": tooltips.aggregateTaxEBITDAGR,
    "Group Ratio % of Tax EBITDA": tooltips.groupRatioTaxEBITDA,
    "Excess debt cap brought forward": tooltips.excessDeptCapGR,
    "Group ratio debt cap (s400(2))": tooltips.groupRatioDeptCap,
    "Basic Interest Allowance (s398(1))": tooltips.basicInterestAllowanceGR,
    "Aggregate Net Tax Interest Income (ANTII)": tooltips.ANTIIGR,
    "Interest allowance (s396(1))": tooltips.interestAllowanceGR,
  };
};

const getDisallowancesAndReactivationsMetrics = (tooltips) => {
  return {
    "Interest Allowance": tooltips.interestAllowanceCDR,
    "Unused interest allowance brought forward":
      tooltips.unusedInterestAllowance,
    "De minimis": tooltips.deMinimis,
    "Interest capacity (Greater of interest allowance plus brought forward allowance and de minimis) (s392)":
      tooltips.interestCapacity,
    "Aggregate Net Tax Interest Expense":
      tooltips.aggregateNetTaxInterestExpense,
    "Disallowed Amount (s373(1)-(2))": tooltips.disallowedAmount,
    "Amount available for Reactivation (Group reactivation cap) (s373(3)-(4))":
      tooltips.groupReactivationCap,
  };
};

const ReviewCalc2 = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const auth = useContext(AuthContext);
  const isReadOnlyOrViewPoA =
    auth?.isReadOnlyUser() || getCurrentPeriodIsReadonly();
  const disableViewModeProgress = disableProgressInViewMode();
  const [applyGroupRatioElection, setApplyGroupRatioElection] = useState(false);
  const [applyNarrc2, setApplyNarrc2] = useState(false);
  const [isError, setIsError] = useState(false);
  const [ultParentName, setUltParentName] = useState("");
  const [firstPoAStart, setFirstPoAStart] = useState();
  const [firstPoAEnd, setFirstPoAEnd] = useState();
  const [groupRatioElectionResult, setGroupRatioElectionResult] =
    useState(false);

  const [tableCDRDataFR, setTableCDRDataFR] = useState([]);
  const [tableCDRDataGR, setTableCDRDataGR] = useState([]);
  const [tableCdrDataFrNar, setTableCdrDataFrNar] = useState([]);
  const [tableCdrDataGrNar, setTableCdrDataGrNar] = useState([]);
  const [displayedCdrTable, setDisplayedCdrTable] = useState([]);

  const tenantId = useSelector((state) => state.usageInfo.tenantID);

  const tooltips = useMemo(() => getPageTooltips("reviewCalc2"), []);
  const tableMaps = useMemo(
    () => ({
      tooltipFRMap: getFixedRatioMetrics(tooltips),
      tooltipGRMap: getGroupRatioMetrics(tooltips),
      tooltipCDRMap: getDisallowancesAndReactivationsMetrics(tooltips),
    }),
    [],
  );

  const [tableFRData, setTableFRData] = useState([
    { title: " " },
    { title: " " },
    { title: "Fixed ratio %", GBP: 500 },
    { title: " " },
    { title: "Aggregate Tax EBITDA", GBP: 0 },
    { title: "Fixed Ratio % of Tax EBITDA", GBP: 0 },
    { title: " " },
    { title: "Adjusted Net Group Interest Expense (ANGIE)", GBP: 0 },
    { title: "Excess debt cap brought forward", GBP: 0 },
    { title: "Fixed Ratio Debt Cap (s400(1)", GBP: 0 },
    { title: " " },
    { title: "Basic Interest Allowance (s397(1))", GBP: 0 },
    { title: "Aggregate Net Tax Interest Income (ANTII)", GBP: 0 },
    { title: "Interest allowance (s396(1))", GBP: 0 },
  ]);
  const [tableGRData, setTableGRData] = useState([
    { title: "Qualifying Net Group Interest Expense (QNGIE)", GBP: 0 },
    { title: "Group EBITDA", GBP: 0 },
    { title: "Group Ratio %", GBP: 0 },
    { title: " " },
    { title: "Aggregate Tax EBITDA", GBP: 0 },
    { title: "Group Ratio % of Tax EBITDA", GBP: 0 },
    { title: " " },
    { title: "Qualifying Net Group Interest Expense (QNGIE)", GBP: 0 },
    { title: "Excess debt cap brought forward", GBP: 0 },
    { title: "Group ratio debt cap (s400(2))", GBP: 0 },
    { title: " " },
    { title: "Basic Interest Allowance (s398(1)", GBP: 0 },
    { title: "Aggregate Net Tax Interest Income (ANTII)", GBP: 0 },
    { title: "Interest allowance (s396(1))", GBP: 0 },
  ]);

  // get calculated values
  const savedData = getGroupData();
  const poaIndex = getPoaIndex();
  const savedPOAData = getExcessDebtCapPriorPeriod(
    savedData,
    savedData?.periods_of_account[poaIndex],
  );
  const displayWarningMessage =
    (applyNarrc2 &&
      applyGroupRatioElection &&
      savedPOAData?.gr_NAR_group_reactivation_cap > 0) ||
    (applyNarrc2 &&
      !applyGroupRatioElection &&
      savedPOAData?.fr_NAR_group_reactivation_cap > 0);
  useEffect(() => {
    if (isReadOnlyOrViewPoA) return;

    if (poaIndex > -1) {
      const poa = getReviewCalc2Values(savedData, poaIndex);
      if (poa) {
        setApplyNarrc2(savedPOAData?.apply_NARRC2 ?? false);
        savedData.periods_of_account[poaIndex] = poa;
        setGroupData(savedData);
      }
    } else {
      toast.error(`could not find POA data`, {
        duration: 3 * 1000, // 3s
      });
    }
  }, []);

  const generateTableRowTitle = (name, column) => {
    const selectedMap = tableMaps["tooltip" + column + "Map"];
    return (
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <span>{name}</span>
        <CustomTooltipIcon tooltipText={selectedMap[name]} />
      </div>
    );
  };

  useEffect(() => {
    const getPoARoundedValueToString = (key) => {
      return Math.round(savedPOAData?.[key] ?? 0)?.toLocaleString();
    };

    // figures and table data set up for Fixed Ratio summary table
    const aggregateTaxEBITDA = savedPOAData?.aggregate_tax_ebitda
      ? savedPOAData?.aggregate_tax_ebitda
      : 0;
    const fixedRatio = savedPOAData?.fr_percentage
      ? (savedPOAData?.fr_percentage * 100).toFixed(2) + "%"
      : 0;
    const fixedRatio30 = savedPOAData?.fr_percentage
      ? aggregateTaxEBITDA * savedPOAData?.fr_percentage
      : 0;

    setTableFRData([
      { title: " " },
      { title: " " },
      { title: generateTableRowTitle("Fixed ratio %", "FR"), GBP: fixedRatio },
      { title: " " },
      {
        title: generateTableRowTitle("Aggregate Tax EBITDA", "FR"),
        GBP: Math.round(aggregateTaxEBITDA).toLocaleString(),
      },
      {
        title: generateTableRowTitle("Fixed Ratio % of Tax EBITDA", "FR"),
        GBP: Math.round(fixedRatio30).toLocaleString(),
      },
      { title: " " },
      {
        title: generateTableRowTitle(
          "Adjusted Net Group Interest Expense (ANGIE)",
          "FR",
        ),
        GBP: getPoARoundedValueToString("adjusted_net_group_interest_expense"),
      },
      {
        title: generateTableRowTitle("Excess debt cap brought forward", "FR"),
        GBP: getPoARoundedValueToString("excess_debt_cap_from_prior_period"),
      },
      {
        title: generateTableRowTitle("Fixed Ratio Debt Cap (s400(1))", "FR"),
        GBP: getPoARoundedValueToString("fr_debt_cap"),
      },
      { title: " " },
      {
        title: generateTableRowTitle(
          "Basic Interest Allowance (s397(1))",
          "FR",
        ),
        GBP: getPoARoundedValueToString("fr_basic_interest_allowance"),
      },
      {
        title: generateTableRowTitle(
          "Aggregate Net Tax Interest Income (ANTII)",
          "FR",
        ),
        GBP: getPoARoundedValueToString("aggregate_net_tax_interest_income"),
      },
      {
        title: generateTableRowTitle("Interest allowance (s396(1))", "FR"),
        GBP: getPoARoundedValueToString("fr_interest_allowance"),
      },
    ]);

    const groupInterestAllowanceQNGIE =
      savedPOAData?.group_ratio_blended_election &&
      savedPOAData?.blended_net_group_interest_expense
        ? Math.round(savedPOAData?.blended_net_group_interest_expense)
        : Math.round(savedPOAData.qualifying_net_group_interest_expense);
    const groupInterestAllowanceEBITDA =
      savedPOAData?.group_ratio_election && savedPOAData?.group_ebitda
        ? Math.round(savedPOAData.group_ebitda)
        : "";
    const groupRatioPercentage = savedPOAData?.gr_percentage
      ? savedPOAData?.gr_percentage
      : 0;
    const groupRationPercentageTaxEBITDA =
      aggregateTaxEBITDA * groupRatioPercentage > 0
        ? Math.round(aggregateTaxEBITDA * groupRatioPercentage * 100) / 100
        : 0;
    setTableGRData([
      {
        title: generateTableRowTitle(
          "Qualifying Net Group Interest Expense (QNGIE)",
          "GR",
        ),
        GBP: Math.round(groupInterestAllowanceQNGIE)?.toLocaleString(),
      },
      {
        title: generateTableRowTitle("Group EBITDA", "GR"),
        GBP: groupInterestAllowanceEBITDA?.toLocaleString(),
      },
      {
        title: generateTableRowTitle("Group Ratio %", "GR"),
        GBP: (groupRatioPercentage * 100)?.toFixed(2) + "%",
      },
      { title: " " },
      {
        title: generateTableRowTitle("Aggregate Tax EBITDA", "GR"),
        GBP: Math.round(aggregateTaxEBITDA)?.toLocaleString(),
      },
      {
        title: generateTableRowTitle("Group Ratio % of Tax EBITDA", "GR"),
        GBP: Math.round(groupRationPercentageTaxEBITDA)?.toLocaleString(),
      },
      { title: " " },
      {
        title: generateTableRowTitle(
          "Qualifying Net Group Interest Expense (QNGIE)",
          "GR",
        ),
        GBP: Math.round(groupInterestAllowanceQNGIE)?.toLocaleString(),
      },
      {
        title: generateTableRowTitle("Excess debt cap brought forward", "GR"),
        GBP: getPoARoundedValueToString("excess_debt_cap_from_prior_period"),
      },
      {
        title: generateTableRowTitle("Group ratio debt cap (s400(2))", "GR"),
        GBP: getPoARoundedValueToString("gr_debt_cap"),
      },
      { title: " " },
      {
        title: generateTableRowTitle(
          "Basic Interest Allowance (s398(1))",
          "GR",
        ),
        GBP: getPoARoundedValueToString("gr_basic_interest_allowance"),
      },
      {
        title: generateTableRowTitle(
          "Aggregate Net Tax Interest Income (ANTII)",
          "GR",
        ),
        GBP: getPoARoundedValueToString("aggregate_net_tax_interest_income"),
      },
      {
        title: generateTableRowTitle("Interest allowance (s396(1))", "GR"),
        GBP: getPoARoundedValueToString("gr_interest_allowance"),
      },
    ]);

    // group interest allowance figures
    const getAllowanceCalculationsValueToString = (key) => {
      return Math.round(
        savedPOAData?.allowance_calculations?.[key] ?? 0,
      )?.toLocaleString();
    };
    // used to populate Computation of Disallowances and REactivatsion section

    const tableRows = [
      { title: generateTableRowTitle("Interest Allowance", "CDR") },
      {
        title: generateTableRowTitle(
          "Unused interest allowance brought forward",
          "CDR",
        ),
      },
      { title: generateTableRowTitle("De minimis", "CDR") },
      {
        title: generateTableRowTitle(
          "Interest capacity (Greater of interest allowance plus brought forward allowance and de minimis) (s392)",
          "CDR",
        ),
      },
      {
        title: generateTableRowTitle(
          "Aggregate Net Tax Interest Expense",
          "CDR",
        ),
      },
      {
        title: generateTableRowTitle("Disallowed Amount (s373(1)-(2))", "CDR"),
      },
      {
        title: generateTableRowTitle(
          "Amount available for Reactivation (Group reactivation cap) (s373(3)-(4))",
          "CDR",
        ),
      },
    ];

    const cdrFrCalculationKeys = [
      { key: "fr_interest_allowance", type: "poaVal" },
      { key: "fr_unused_allowance", type: "allowanceCalculationVal" },
      { key: "interest_capacity_de_minimis", type: "poaVal" },
      { key: "fr_interest_capacity", type: "poaVal" },
      { key: "aggregate_net_tax_interest_expense", type: "poaVal" },
      { key: "fr_disallowance", type: "poaVal" },
      { key: "fr_group_reactivation_cap", type: "poaVal" },
    ];
    setTableCDRDataFR(
      tableRows.map((row, index) => ({
        title: row.title,
        GBP:
          cdrFrCalculationKeys[index].type === "poaVal"
            ? getPoARoundedValueToString(cdrFrCalculationKeys[index].key)
            : getAllowanceCalculationsValueToString(
                cdrFrCalculationKeys[index].key,
              ),
      })),
    );

    const cdrGrCalculationKeys = [
      { key: "gr_interest_allowance", type: "poaVal" },
      { key: "gr_unused_allowance", type: "allowanceCalculationVal" },
      { key: "interest_capacity_de_minimis", type: "poaVal" },
      { key: "gr_interest_capacity", type: "poaVal" },
      { key: "aggregate_net_tax_interest_expense", type: "poaVal" },
      { key: "gr_disallowance", type: "poaVal" },
      { key: "gr_group_reactivation_cap", type: "poaVal" },
    ];
    setTableCDRDataGR(
      tableRows.map((row, index) => ({
        title: row.title,
        GBP:
          cdrGrCalculationKeys[index].type === "poaVal"
            ? getPoARoundedValueToString(cdrGrCalculationKeys[index].key)
            : getAllowanceCalculationsValueToString(
                cdrGrCalculationKeys[index].key,
              ),
      })),
    );

    const cdrFrNarCalculationKeys = [
      { key: "fr_interest_allowance", type: "poaVal" },
      { key: "fr_unused_allowance", type: "allowanceCalculationVal" },
      { key: "interest_capacity_de_minimis", type: "poaVal" },
      { key: "fr_NAR_interest_capacity", type: "poaVal" },
      { key: "aggregate_net_tax_interest_expense", type: "poaVal" },
      { key: "fr_NAR_disallowance", type: "poaVal" },
      { key: "fr_NAR_group_reactivation_cap", type: "poaVal" },
    ];
    setTableCdrDataFrNar(
      tableRows.map((row, index) => ({
        title: row.title,
        GBP:
          cdrFrNarCalculationKeys[index].type === "poaVal"
            ? getPoARoundedValueToString(cdrFrNarCalculationKeys[index].key)
            : "0",
        key: cdrFrNarCalculationKeys[index].key,
      })),
    );

    const cdrGrNarCalculationKeys = [
      { key: "gr_interest_allowance", type: "poaVal" },
      { key: "gr_unused_allowance", type: "allowanceCalculationVal" },
      { key: "interest_capacity_de_minimis", type: "poaVal" },
      { key: "gr_NAR_interest_capacity", type: "poaVal" },
      { key: "aggregate_net_tax_interest_expense", type: "poaVal" },
      { key: "gr_NAR_disallowance", type: "poaVal" },
      { key: "gr_NAR_group_reactivation_cap", type: "poaVal" },
    ];
    setTableCdrDataGrNar(
      tableRows.map((row, index) => ({
        title: row.title,
        GBP:
          cdrGrNarCalculationKeys[index].type === "poaVal"
            ? getPoARoundedValueToString(cdrGrNarCalculationKeys[index].key)
            : "0",
        key: cdrGrNarCalculationKeys[index].key,
      })),
    );
  }, []);

  const updateCRTableData = (applyGroupRatioElection) => {
    setApplyGroupRatioElection(!applyGroupRatioElection);
  };

  const toggleNarrc2 = () => {
    setApplyNarrc2((prevState) => !prevState);
  };

  useEffect(() => {
    (async () => {
      const savedData = getGroupData(); // Having the getGroupData() call in line 28 meant it didn't load for some reason?
      if (
        savedPOAData?.fr_interest_allowance >
        savedPOAData?.gr_interest_allowance
      ) {
        setGroupRatioElectionResult(false);
      } else if (
        savedPOAData?.fr_interest_allowance <
        savedPOAData?.gr_interest_allowance
      ) {
        setGroupRatioElectionResult(true);
      }
      // set local state variables based on getGroupData
      setUltParentName(savedData?.ult_parent_name || "");
      setFirstPoAStart(
        processDateJS(savedData?.model_first_period_start)?.format(
          "DD/MM/YYYY",
        ),
      );
      setFirstPoAEnd(
        processDateJS(savedData?.model_first_period_end)?.format("DD/MM/YYYY"),
      );
      setApplyGroupRatioElection(savedPOAData?.apply_group_ratio_election);
      updateCRTableData(!savedPOAData?.apply_group_ratio_election);
      if (!savedData || isReadOnlyOrViewPoA) {
        return;
      }

      var companies = savedData?.companies;
      // If companies is still 0, maybe it errored out above?
      if (!companies) {
        companies = [];
      }
      setIsLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (applyGroupRatioElection && applyNarrc2) {
      setDisplayedCdrTable(tableCdrDataGrNar);
    } else if (applyGroupRatioElection && !applyNarrc2) {
      setDisplayedCdrTable(tableCDRDataGR);
    } else if (!applyGroupRatioElection && applyNarrc2) {
      setDisplayedCdrTable(tableCdrDataFrNar);
    } else {
      setDisplayedCdrTable(tableCDRDataFR);
    }
  }, [
    applyGroupRatioElection,
    applyNarrc2,
    tableCDRDataFR,
    tableCDRDataGR,
    tableCdrDataFrNar,
    tableCdrDataGrNar,
  ]);

  const save = async () => {
    if (isError) {
      throw "There is an error. Please fix before proceeding...";
    }

    if (isReadOnlyOrViewPoA) {
      return;
    }

    if (poaIndex > -1) {
      const poa = getReviewCalc2Values(savedData, poaIndex);
      if (poa) {
        savedData.periods_of_account[poaIndex] = poa;
        setGroupData(savedData);
      }
    }

    setIsPosting(true);

    if (poaIndex < 0) {
      toast.error(`could not find POA data`, {
        duration: 3 * 1000, // 3s
      });
    }

    let updatedData = getGroupData();
    const canProgressCalc = canProgressCalculation();

    // set group values based on the GR/FR election
    const getValHelper = (data, key) => {
      const grKey = `gr_${key}`;
      const frKey = `fr_${key}`;

      if (applyGroupRatioElection && applyNarrc2) {
        const grNarKey = `gr_NAR_${key}`;
        return data?.[grNarKey] ?? data?.[grKey];
      } else if (applyGroupRatioElection && !applyNarrc2) {
        return data?.[grKey];
      } else if (!applyGroupRatioElection && applyNarrc2) {
        const frNarKey = `fr_NAR_${key}`;
        return data?.[frNarKey] ?? data?.[frKey];
      } else {
        return data?.[frKey];
      }
    };
    const getCdrValue = (key) => {
      return getValHelper(savedPOAData, key);
    };
    const getCdrAllowanceCalculationsValue = (key) => {
      return getValHelper(savedPOAData?.allowance_calculations, key);
    };
    const newPOA = {
      ...savedPOAData,
      apply_group_ratio_election: applyGroupRatioElection,
      ratio_type: applyGroupRatioElection ? "Group Ratio" : "Fixed Ratio",
      group_basic_interest_allowance: Math.round(
        getCdrValue("basic_interest_allowance"),
      ),
      group_interest_allowance: Math.round(getCdrValue("interest_allowance")),
      group_debt_cap: Math.round(getCdrValue("debt_cap")),
      group_percentage: getCdrValue("percentage"),
      group_interest_capacity: Math.round(getCdrValue("interest_capacity")),
      group_bf_interest_allowance: getCdrValue("bf_interest_allowance_used"),
      group_unexpired_allowance: getCdrAllowanceCalculationsValue(
        "unexpired_allowance",
      ),
      group_unused_allowance: Math.round(
        getCdrAllowanceCalculationsValue("unused_allowance"),
      ),
      group_disallowance: Math.round(getCdrValue("disallowance")),
      group_excess_debt_cap: Math.round(
        getCdrValue("excess_debt_cap_generated"),
      ),
      status_reviewcalc2_completed: true,
      ...canProgressCalc,
    };

    newPOA.group_excess_debt_cap =
      newPOA?.group_excess_debt_cap ??
      Math.max(
        Math.min(
          newPOA?.group_debt_cap -
            newPOA?.group_percentage *
              newPOA?.aggregate_net_tax_interest_expense,
          0,
        ),
        savedPOAData?.excess_debt_cap_from_prior_period +
          newPOA?.group_disallowance,
      );
    newPOA.group_interest_reactivation_cap = Math.max(
      newPOA?.group_interest_allowance -
        newPOA?.aggregate_net_tax_interest_expense,
      0,
    );
    newPOA.period_unused_interest_allowance = Math.max(
      newPOA?.group_interest_allowance -
        newPOA?.aggregate_net_tax_interest_expense,
      0,
    );
    const totalBFD = getBFDTotal("bfd", newPOA);
    const totalBFDADJ = getBFDTotal("bfdadj", newPOA);
    newPOA.group_interest_reactivation_available = Math.min(
      newPOA?.group_interest_reactivation_cap,
      totalBFD + totalBFDADJ,
    );
    newPOA.unused_generated = Math.max(
      newPOA?.group_interest_allowance -
        newPOA?.aggregate_net_tax_interest_expense -
        newPOA?.group_interest_reactivation_available,
      0,
    );

    if (!newPOA?.apply_group_ratio_election) {
      newPOA.group_ratio_blended_election = false;

      if (
        (processDateJS(savedData?.group_ebitda_gains_year)?.isSame(
          newPOA?.period_start,
          "day",
        ) ||
          processDateJS(savedData?.group_ebitda_gains_year)?.isAfter(
            newPOA?.period_start,
          )) &&
        (processDateJS(savedData?.group_ebitda_gains_year)?.isSame(
          newPOA?.period_end,
          "day",
        ) ||
          processDateJS(savedData?.group_ebitda_gains_year)?.isBefore(
            newPOA?.period_end,
          ))
      ) {
        updatedData.group_ebitda_gains = false;
        updatedData.group_ebitda_gains_year = null;
      }
    }

    newPOA.apply_NARRC2 = Boolean(applyNarrc2);

    newPOA.shouldZeroRemainingAllocation =
      applyNarrc2 || !!newPOA.shouldZeroRemainingAllocation;

    if (applyNarrc2) {
      if (applyGroupRatioElection)
        newPOA.allowance_calculations.gr_unused_allowance = 0;
      else newPOA.allowance_calculations.fr_unused_allowance = 0;
    }

    if (poaIndex === -1) {
      throw `could not find POA data`;
    }

    updatedData.periods_of_account[poaIndex] = newPOA;
    // allocate allowance to previous unused amounts
    const newJson = calculateUsedAllowances(updatedData, poaIndex);

    const poa = newJson.periods_of_account[poaIndex];

    const { unused_allowances_for_group, furthest_page_visited } = newJson;
    const postData = {
      id: getGroupID(),
      poa,
      poaIndex,
      unusedAllowancesForGroup: unused_allowances_for_group,
      furthest_page_visited,
    };

    console.log("Posting review calc 2 data: ", tenantId);
    const promise = sqlService.postReviewCalc24(tenantId, postData);

    const onSuccess = () => {
      setGroupData(newJson);
      setIsPosting(false);
    };
    const onError = () => setIsPosting(false);
    return toast.save(promise, onSuccess, onError);
  };

  const renderCell = (row, field) => {
    const val = row[field];
    const isRed =
      row?.key?.includes("group_reactivation_cap") &&
      applyNarrc2 &&
      val &&
      Number(val.replaceAll(",", "")) > 0;
    return <span style={{ ...(isRed ? { color: "red" } : {}) }}>{val}</span>;
  };

  if (isLoading)
    return (
      <Loading
        loadingType="linear"
        indeterminate={true}
        compact={false}
        className="page-loader"
      ></Loading>
    );

  return (
    <div className="ap-container">
      <div className="ap-container mt-4">
        <CompanyPanel />
      </div>
      <p></p>

      <Panel>
        <RevCalcHeader groupname="Review Calculations - Fixed/Group Ratio Election" />
        <ReviewCalcNav currentStep={1} />
        <div className="col" style={{ padding: "0em 1em" }}>
          <p>
            This page summarises the output of the CIR calculations, and allows
            you to choose whether or not to apply the Group Ratio election for
            the period.
          </p>
        </div>

        <div className="table-container">
          <div className="col-6" style={{ padding: "0em 0.5em 0em 1em" }}>
            <Table originalData={tableFRData} hasTitle striped>
              <Column field="title">FIXED RATIO COMPUTATION</Column>
              <Column field="GBP">GBP</Column>
            </Table>
          </div>
          <div className="col-6" style={{ padding: "0em 1em 0em 0.5em" }}>
            {savedPOAData?.group_ratio_election && (
              <Table originalData={tableGRData} hasTitle striped>
                <Column field="title">GROUP RATIO COMPUTATION</Column>
                <Column field="GBP">GBP</Column>
              </Table>
            )}
          </div>
        </div>
        <div style={{ padding: "1.5em 1em" }}>
          {savedPOAData?.group_ratio_election && (
            <div style={{ padding: "1em 1em" }} className="row">
              <div className="ap-switch col-6">
                <span>
                  Does applying the group ratio election result in a higher
                  interest allowance?
                </span>
                <CustomTooltipIcon
                  size="big"
                  tooltipText={tooltips.groupRatioHigherInterestAllowance}
                />
              </div>
              {groupRatioElectionResult ? (
                <Badge className="col-1" type="success-outlined" value="Yes" />
              ) : (
                <Badge className="col-1" type="danger-outlined" value="No " />
              )}
            </div>
          )}
          <div style={{ padding: "1em 1em" }} className="row">
            <div className="ap-switch col-6">
              <span>Apply Group Ratio Election?</span>
              <CustomTooltipIcon tooltipText={tooltips.applyGroupRatio} />
            </div>
            <Tooltip
              content="This switch is disabled because Group Ratio Election calculation was disabled in the Group Data tab."
              disabled={!savedPOAData?.group_ratio_election ? false : true}
            >
              <div className="col-1" style={{ width: 50, height: 40 }}>
                <Switch
                  className="col-1"
                  style={{ width: "fit-content" }}
                  showIndicator
                  disabled={!savedPOAData?.group_ratio_election}
                  checked={applyGroupRatioElection}
                  defaultChecked={applyGroupRatioElection}
                  onChange={() => updateCRTableData(applyGroupRatioElection)}
                />
              </div>
            </Tooltip>
          </div>
          <div style={{ padding: "1em 1em" }} className="row">
            <div className="ap-switch col-6">
              <span>
                Prepare for an Abbreviated return or finalising the period
                without Reporting Company
              </span>
              <CustomTooltipIcon
                tooltipText={tooltips.prepareAbbreviatedReturn}
              />
            </div>

            <div className="col-1 " style={{ height: 40 }}>
              <Switch
                className="col-1"
                style={{ width: "fit-content" }}
                showIndicator
                checked={applyNarrc2}
                defaultChecked={applyNarrc2}
                onChange={toggleNarrc2}
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col" style={{ padding: "0em 2em 0em 2em" }}>
            <Table originalData={displayedCdrTable} hasTitle striped>
              <Column field="title">
                COMPUTATION OF DISALLOWANCES AND REACTIVATIONS
              </Column>
              <Column field="GBP" renderCell={renderCell}>
                GBP
              </Column>
            </Table>
            {displayWarningMessage && (
              <p style={{ color: "red" }}>
                Reactivation amounts will not be available if an abbreviated
                return is filed or if the period is finalised without a
                Reporting Company.
              </p>
            )}
          </div>
        </div>

        <div className="container-fluid mt-4">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <BackButton disabled={isPosting} />
            </div>
            <div className="d-flex gap-2">
              <Button
                hiddenInReadonly
                onClick={save}
                loading={isPosting}
                kind="secondary"
              >
                Save
              </Button>
              <div className="col-1">
                <NextButton
                  loading={isPosting}
                  disabled={disableViewModeProgress}
                  preNavigation={save}
                >
                  Next
                </NextButton>
              </div>
            </div>
          </div>
        </div>
      </Panel>
    </div>
  );
};

export default ReviewCalc2;
